import React from "react";
import { motion } from "framer-motion";
import { Target, Shield, Globe2, Headphones, ArrowRight } from "lucide-react";
import { SectionHeader } from "@/components/sections/Services";
import { HOME } from "@/constants/testIds";

const audienceTargeting = [
  "Demographic targeting",
  "Behavioral segmentation",
  "Interest-based targeting",
  "Lookalike audiences",
];
const contextualTargeting = [
  "Content categorization",
  "Keyword targeting",
  "Seasonal campaigns",
  "Brand safety controls",
];

const whyChoose = [
  {
    icon: Target,
    title: "Precise",
    subtitle: "Targeting",
    text: "Advanced audience targeting for maximum engagement and conversion rates",
  },
  {
    icon: Shield,
    title: "Brand",
    subtitle: "Safety",
    text: "Premium brand safety measures and comprehensive fraud protection",
  },
  {
    icon: Globe2,
    title: "Global",
    subtitle: "Reach",
    text: "Worldwide coverage with local market expertise and cultural insights",
  },
  {
    icon: Headphones,
    title: "Expert",
    subtitle: "Support",
    text: "Dedicated account management and 24/7 technical support",
  },
];

export default function Advertisers() {
  return (
    <section
      id="advertisers"
      data-testid={HOME.advertisersSection}
      className="relative py-32"
    >
      <div className="container-x">
        <SectionHeader
          eyebrow="For Advertisers"
          title={
            <>
              Reach
              <br />
              <span className="aurora-text">Your Target Audience</span>
            </>
          }
          subtitle="Connect with your ideal customers through our premium publisher network and advanced targeting capabilities."
        />

        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 mb-24">
          <div className="tile p-8 md:p-12">
            <h3 className="font-display text-3xl md:text-4xl font-bold mb-4">
              Precision
              <br />
              <span className="cyan-text">Targeting</span>
            </h3>
            <p className="text-white/65 mb-8 max-w-2xl">
              Leverage our advanced targeting capabilities to reach the right
              audience at the right time with the right message. Our platform
              combines first-party data with sophisticated algorithms to deliver
              exceptional campaign performance.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="text-sm uppercase tracking-[0.2em] text-[#00F0FF] mb-4">
                  Audience Targeting
                </h4>
                <ul className="space-y-2">
                  {audienceTargeting.map((t) => (
                    <li key={t} className="text-sm text-white/75 flex gap-2">
                      <span className="text-[#00F0FF]">•</span> {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-sm uppercase tracking-[0.2em] text-[#00F0FF] mb-4">
                  Contextual Targeting
                </h4>
                <ul className="space-y-2">
                  {contextualTargeting.map((t) => (
                    <li key={t} className="text-sm text-white/75 flex gap-2">
                      <span className="text-[#00F0FF]">•</span> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <a
              href="/signup"
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
            >
              Start Your Campaign <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="space-y-6">
            <div className="tile p-7">
              <h4 className="text-sm uppercase tracking-[0.2em] text-white/50 mb-5">
                Campaign Formats
              </h4>
              <div className="space-y-3">
                {["Display", "Mobile", "Video"].map((fmt) => (
                  <div
                    key={fmt}
                    className="flex items-center justify-between p-4 rounded-xl border border-white/5 hover:border-[#00F0FF]/40 transition-colors"
                  >
                    <span className="font-medium">{fmt}</span>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400">
                      Available
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="tile p-7">
              <h4 className="font-display text-xl font-bold mb-1">
                Performance
                <span className="cyan-text"> Metrics</span>
              </h4>
              <p className="text-xs text-white/40 mb-5">Across our network</p>
              <div className="space-y-4">
                <Metric label="Average CTR" value="2.8%" />
                <Metric label="Conversion Rate" value="4.2%" />
                <Metric label="Brand Lift" value="+35%" />
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Grid */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-display text-4xl md:text-5xl font-bold">
            Why Choose <span className="cyan-text">MonetiseUP?</span>
          </h2>
          <p className="text-white/65 mt-4">
            Industry-leading features designed to maximize your advertising
            success and deliver exceptional results.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {whyChoose.map((w, i) => (
            <motion.div
              key={w.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="tile p-7 group"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0047FF]/30 to-[#00F0FF]/20 border border-white/10 grid place-items-center mb-4 group-hover:scale-110 transition-transform">
                <w.icon className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <h3 className="font-display text-xl font-bold">
                {w.title}
                <span className="block text-sm text-white/50 font-normal">
                  {w.subtitle}
                </span>
              </h3>
              <p className="text-sm text-white/60 mt-3">{w.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Metric({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-white/55">{label}</span>
      <span className="font-mono text-lg font-bold text-white">{value}</span>
    </div>
  );
}
